import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import HeaderOrg from "../Headers/HeaderOrg";
import ManageTitle from "../../Components/TitleElements/ManageTitle";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import ToggleSwitch from "../../Components/ToggleSwitch/ToggleSwitch";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import PublishConfirm, { publishAsk } from "./PublishConfirm";
import { useJobDevice } from "../../device/DeviceContext";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getDrawDashboard,
  getResults,
  publishRecall,
  publishResults,
  publishPlaces,
  drawRoundAward,
  setNaidcExtra,
  setResultsMasterGate,
  checkResult,
  downloadEventDayPdf,
} from "../../network/oireachtasEventDay";

// B3 — results & recalls (tabulation-and-results.md §7–8): live standings
// with recall list, placements, WQ/NAIDC qualification and round awards; the
// recall/results publish actions (gated on adjudicator sign-off — ToggleSwitch
// like the feis ResultStatus screen, with the gate enforced on toggle); seeded
// round-award judge draws; and the admin-set dancer status flags
// (worldMedalHolder / naidcQualified / wqDeferred — locked §13.1).
const OireachtasResults = () => {
  const [event, setEvent] = useState(null);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null); // getResults payload
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes, dash] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          getDrawDashboard(eventId),
        ]);
        setEvent(eventRes.event);
        setRows(dash.rows || []);
        // Deep-link from the Status screen: ?comp=<id> preselects and loads.
        const compParam = new URLSearchParams(location.search).get("comp");
        if (compParam && (dash.rows || []).some((r) => String(r.competition._id) === compParam)) {
          selectComp(compParam);
        }
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load results");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadResults = async (compId) => {
    try {
      setBusy(true);
      setData(await getResults(eventId, compId));
    } catch (e) {
      setData(null);
      ShowToastMessage("error", e?.messages || "Lock the draw before tabulating");
    } finally {
      setBusy(false);
    }
  };

  const selectComp = (compId) => {
    setSelected(compId);
    setData(null);
    if (compId) loadResults(compId);
  };

  // every publish action asks first: { title, body, label, run }
  const [ask, setAsk] = useState(null);
  // On an event-day device (card 540f21a4) the job decides what shows:
  // publishing and unpublishing (each its own function) and drawing round
  // awards. The family-results switch and NAIDC additions are never a device's.
  const { device: jobDevice, can } = useJobDevice();
  const canToggle = (kind, published) => can(`ox.${kind}.${published ? "unpublish" : "publish"}`);
  const canAward = can("ox.roundawards");
  // Check results may make a round's FIRST award draw, not re-draw it
  // (MC review 5ce49840 / c87cf2d7 R1-F1)
  const canFirstDraw = canAward || can("ox.results.check");
  // a device whose job checks results checks them here too (it may not open
  // Enter Scores — the Podium job; MC review c87cf2d7 R1-F4)
  const checkHere = !!jobDevice && can("ox.results.check");
  const answerAsk = () => {
    const run = ask && ask.run;
    setAsk(null);
    // the job is re-read every minute: a confirmation opened before a
    // function was unticked doesn't send it (MC review c87cf2d7 R3-F2)
    if (ask && ask.fn && !can(ask.fn)) return;
    if (run) run();
  };
  const askFn = ask && ask.fn;
  const askAllowed = !askFn || can(askFn);
  useEffect(() => {
    if (!askAllowed) setAsk(null);
  }, [askAllowed]);

  const act = async (fn, okMsg) => {
    try {
      setBusy(true);
      const res = await fn();
      ShowToastMessage("success", res.message || okMsg);
      await loadResults(selected);
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Action failed");
      setBusy(false);
    }
  };

  // Publish toggles follow the feis ResultStatus ToggleSwitch pattern; the
  // sign-off gate is enforced here (and server-side) with an explanatory toast.
  const toggleRecall = () => {
    if (!data.recall.published && data.gate.recallReady !== true) {
      ShowToastMessage("error", "R1+R2 sheets must be signed off before publishing the recall");
      return;
    }
    setAsk({
      fn: data.recall.published ? "ox.recall.unpublish" : "ox.recall.publish",
      ...publishAsk("recall", data.competition.code, !data.recall.published),
      run: () => act(() => publishRecall(eventId, selected, data.recall.published), "Recall updated"),
    });
  };

  const toggleResults = () => {
    if (!data.resultsPublished && !data.gate.resultsReady) {
      ShowToastMessage("error", "All sheets must be signed off before publishing results");
      return;
    }
    setAsk({
      fn: data.resultsPublished ? "ox.results.unpublish" : "ox.results.publish",
      ...publishAsk("results", data.competition.code, !data.resultsPublished),
      run: () => act(() => publishResults(eventId, selected, data.resultsPublished), "Results updated"),
    });
  };

  // a one-round competition's places list, before its results (Devin
  // 2026-11-04): published with the results' functions; not over published results
  const placesOn = !!(data && data.places && data.places.published);
  const togglePlaces = () => {
    if (!placesOn && data.resultsPublished) {
      ShowToastMessage("error", "The results are published — the places are part of them");
      return;
    }
    if (!placesOn && !data.gate.resultsReady) {
      ShowToastMessage("error", "All sheets must be signed off before publishing the places");
      return;
    }
    setAsk({
      fn: placesOn ? "ox.results.unpublish" : "ox.results.publish",
      ...publishAsk("places", data.competition.code, !placesOn),
      run: () => act(() => publishPlaces(eventId, selected, placesOn), "Places updated"),
    });
  };

  // B4: the event-level master gate. Per-comp publishing feeds the PUBLIC
  // results page immediately (recall lists are read in the hall mid-event);
  // families see nothing on the parent view until this flips.
  const toggleMasterGate = async () => {
    try {
      setBusy(true);
      const res = await setResultsMasterGate(eventId, !event.resultsPublished);
      setEvent({ ...event, resultsPublished: res.resultsPublished });
      ShowToastMessage("success", res.message || "Master gate updated");
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not update the master gate");
    } finally {
      setBusy(false);
    }
  };

  const selectedRow = () => rows.find((r) => String(r.competition._id) === String(selected));
  const eventDate = (event?.eventDays && event.eventDays[0]) || event?.entriesCloseDate;

  // Compact status text for the standings table (house tables carry text, not
  // pill badges): "Trophy · R · WQ · R1 award".
  const statusText = (r) => {
    const parts = [];
    if (r.placed) parts.push(r.trophy ? "Trophy" : "Placed");
    if (r.recalled) parts.push("R");
    if (r.wq) parts.push("WQ");
    if (r.wqAuto) parts.push("WQ auto");
    if (r.naidcQ) parts.push("NAIDC");
    // Failed To Complete: no place, the places below close up (the standard
    // in every published result book). A recalled dancer who simply did not
    // dance round 3 is not FTC: they place on their round 1 + 2 points.
    if (r.ftc) {
      parts.push(r.medalBlocked ? "FTC round 3" : r.recallIneligible ? "FTC" : "FTC (missed a round)");
    } else if (r.absent && r.recalled) {
      parts.push("did not dance R3");
    }
    (r.roundAwards || []).forEach((rr) => parts.push(`R${rr} award`));
    return parts.join(" · ");
  };

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <ManageTitle
        eventLogo={event?.logo}
        eventName={event?.name}
        eventType={event?.eventType}
        eventDate={eventDate}
        loading={loading}
        onBackClick={() => setTimeout(() => navigate(`/oireachtas/manage?id=${eventId}`), 200)}
      />
      {!loading && (
        <div className="db-body">
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            Recalls, placements and qualification are recomputed live from the judges' scores.
            Publishing is gated on adjudicator sign-off. Qualification status flags are marked
            on the Competitors screen.
          </div>

          {event && !jobDevice && (
            <div className="d-flex align-items-center gap-2 mt-3">
              <span className="search-label">Family results (parent view)</span>
              <ToggleSwitch
                checked={!!event.resultsPublished}
                onChange={() => setAsk({ ...publishAsk("families", "", !event.resultsPublished), run: toggleMasterGate })}
              />
              <span className="grey-42" style={{ fontSize: "12px" }}>
                {event.resultsPublished
                  ? "Families can see their dancers' published results"
                  : "Hidden from families until switched on — published comps still show on the public results page"}
              </span>
            </div>
          )}

          <div className="d-md-flex mt-4 gap-md-4">
            <div>
              <div className="search-label">Select a competition:</div>
              <select
                className="check-in-select"
                value={selected}
                onChange={(e) => selectComp(e.target.value)}
              >
                <option value="">Select…</option>
                {rows.map((r) => {
                  const ready = r.draw && r.draw.locked;
                  return (
                    <option key={r.competition._id} value={r.competition._id} disabled={!ready}>
                      {r.competition.code} —{" "}
                      {ready ? `${r.entrantCount} entrants` : "draw not locked"}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* ---- RESULTS ---- */}
          {selected && !busy && data && (
            <>
              <div className="d-flex flex-wrap align-items-center gap-4 mt-4">
                {data.recall.applicable && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="search-label">Recall published</span>
                    <ToggleSwitch
                      checked={!!data.recall.published}
                      onChange={toggleRecall}
                      disabled={!canToggle("recall", !!data.recall.published)}
                    />
                  </div>
                )}
                {data.places && data.places.applicable && (
                  <div className="d-flex align-items-center gap-2">
                    <span className="search-label">Places published</span>
                    <ToggleSwitch
                      checked={placesOn}
                      onChange={togglePlaces}
                      disabled={!canToggle("results", placesOn)}
                    />
                  </div>
                )}
                <div className="d-flex align-items-center gap-2">
                  <span className="search-label">Results published</span>
                  <ToggleSwitch
                    checked={!!data.resultsPublished}
                    onChange={toggleResults}
                    disabled={!canToggle("results", !!data.resultsPublished)}
                  />
                </div>
                {/* round awards: rounds 1 and 2 only (Devin 2026-10-21) */}
                {Array.from({ length: Math.min(2, data.competition.rounds) }, (_, i) => i + 1).map((r) => {
                  const draw = data.roundAwardDraws.find((d) => d.round === r);
                  if (!canAward && !(canFirstDraw && !draw)) {
                    return (
                      <span key={r} className="grey-42">
                        {draw ? `R${r} award: Judge ${draw.judgeSlot}` : `R${r} award not drawn yet`}
                      </span>
                    );
                  }
                  return (
                    <button
                      key={r}
                      style={{ padding: 0 }}
                      className={busy ? "text-btn-disabled" : "text-btn"}
                      title={draw ? `seed ${draw.seed}` : "seeded draw, audited"}
                      disabled={busy}
                      onClick={() =>
                        act(() => drawRoundAward(eventId, selected, r), "Round award drawn")
                      }
                    >
                      {draw ? `R${r} award: Judge ${draw.judgeSlot} ↻` : `Draw R${r} award judge`}
                    </button>
                  );
                })}
              </div>

              {checkHere && (
                <div className="d-flex flex-column gap-2 mt-3" style={{ fontSize: "13px" }}>
                  {[
                    // a stage shows while it needs checking, and stays for its
                    // prints once published (MC review c87cf2d7 R1-F4)
                    ...(data.recall.applicable
                      ? [{ stage: "recall", ready: data.gate.recallReady === true, label: "recall", published: !!data.recall.published }]
                      : []),
                    ...(!data.recall.applicable || data.recall.published
                      ? [{ stage: "results", ready: !!data.gate.resultsReady, label: "results", published: !!data.resultsPublished }]
                      : []),
                  ].map(({ stage, ready, label, published }) => {
                    const check = (data.checks && data.checks[stage]) || {};
                    const print = (sheet) =>
                      act(
                        () =>
                          downloadEventDayPdf(
                            eventId,
                            selected,
                            `announce.pdf?stage=${stage}${sheet ? `&sheet=${sheet}` : ""}`,
                            `${data.competition.code}-${sheet || stage}.pdf`
                          ).then(() => ({ message: "Printed" })),
                        "Printed"
                      );
                    return (
                      <div key={stage} className="d-flex flex-wrap align-items-center gap-3">
                        {published ? (
                          <span className="grey-30">The {label} {label === "results" ? "are" : "is"} published</span>
                        ) : check.checked ? (
                          <span className="grey-30">
                            The {label} {label === "results" ? "are" : "is"} checked by {check.by || "a tabulator"}
                          </span>
                        ) : (
                          <button
                            type="button"
                            style={{ padding: 0 }}
                            className={busy || !ready ? "text-btn-disabled" : "text-btn"}
                            disabled={busy || !ready}
                            onClick={() =>
                              act(
                                () => checkResult(eventId, selected, stage, data.versions && data.versions[stage]),
                                `${label === "results" ? "Results" : "Recall"} checked`
                              )
                            }
                          >
                            {ready ? `I've checked this ${label}` : `The ${label} can be checked once its sheets are signed off`}
                          </button>
                        )}
                        {(check.checked || published) && stage === "recall" && (
                          <button type="button" style={{ padding: 0 }} className="text-btn" disabled={busy} onClick={() => print("")}>
                            Print recall list
                          </button>
                        )}
                        {(check.checked || published) && stage === "results" && (
                          <>
                            {data.places && data.places.applicable && (
                              <button
                                type="button"
                                style={{ padding: 0 }}
                                className="text-btn"
                                disabled={busy}
                                onClick={() =>
                                  act(
                                    () =>
                                      downloadEventDayPdf(eventId, selected, "announce.pdf?stage=places", `${data.competition.code}-places.pdf`).then(
                                        () => ({ message: "Printed" })
                                      ),
                                    "Printed"
                                  )
                                }
                              >
                                Print places
                              </button>
                            )}
                            <button type="button" style={{ padding: 0 }} className="text-btn" disabled={busy} onClick={() => print("announcer")}>
                              Print for announcer
                            </button>
                            <button type="button" style={{ padding: 0 }} className="text-btn" disabled={busy} onClick={() => print("")}>
                              Print full results
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {(data.strayScores || []).some((x) => x.reason === "not_recalled") && (
                <div className="grey-30 mt-3" role="alert" style={{ fontSize: "13px" }}>
                  <b>Recall-round scores entered for dancers who were not recalled:</b>{" "}
                  {data.strayScores
                    .filter((x) => x.reason === "not_recalled")
                    .map((x) => `#${x.card} (round ${x.round})`)
                    .join(", ")}
                  . Not counted — publishing is blocked until those scores are corrected (a mistyped
                  number) or cleared.
                </div>
              )}

              <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
                Danced:{" "}
                {data.dancedCounts.map((d) => `R${d.round} ${d.n}`).join(" · ")}
                {data.recall.applicable &&
                  ` — recall ${data.recall.recallAll ? "all" : data.recall.count} (${data.recall.recalled.length} incl. ties)`}
                {" — placed "}
                {data.placement.placedCount}
                {data.competition.qualifier &&
                  ` — WQ ${data.qualification.wqCount} · NAIDC ${data.qualification.naidcCount}`}
                {data.qualification.naidcExtra > 0 && ` + ${data.qualification.naidcExtra} added`}
              </div>

              {/* Rule 12 plus the regional director's discretion to add recalled
                  dancers (e.g. 15 qualify, 16 recalled: add the 16th). Not
                  limited, but never past the recalled. */}
              {data.competition.qualifier &&
                (data.qualification.naidcLeftOut > 0 || data.qualification.naidcExtraSet > 0) && (() => {
                  const q = data.qualification;
                  const set = q.naidcExtraSet || 0;
                  const locked = busy || data.resultsPublished || !!jobDevice;
                  const setExtra = (n) =>
                    act(() => setNaidcExtra(eventId, selected, n), "NAIDC qualifiers updated");
                  return (
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-2" style={{ fontSize: "13px" }}>
                      <span className="grey-30">
                        {q.naidcLeftOut} recalled {q.naidcLeftOut === 1 ? "dancer does" : "dancers do"} not
                        qualify for NAIDC under Rule 12. Director adds the next:
                      </span>
                      {/* steps the SAVED setting (naidcExtraSet), so a setting
                          that no longer applies can still be cleared */}
                      <button
                        style={{ padding: 0 }}
                        className={locked || set <= 0 ? "text-btn-disabled" : "text-btn"}
                        disabled={locked || set <= 0}
                        aria-label="One fewer NAIDC qualifier"
                        onClick={() => setExtra(Math.min(set - 1, q.naidcLeftOut))}
                      >
                        −
                      </button>
                      <b>{set}</b>
                      <button
                        style={{ padding: 0 }}
                        className={locked || set >= q.naidcLeftOut ? "text-btn-disabled" : "text-btn"}
                        disabled={locked || set >= q.naidcLeftOut}
                        aria-label="One more NAIDC qualifier"
                        onClick={() => setExtra(set + 1)}
                      >
                        +
                      </button>
                      {q.naidcExtra < set && (
                        <span className="grey-42">
                          only {q.naidcExtra} {q.naidcExtra === 1 ? "applies" : "apply"} — no more recalled dancers
                        </span>
                      )}
                      {data.resultsPublished && (
                        <span className="grey-42">Unpublish the results to change this.</span>
                      )}
                    </div>
                  );
                })()}

              <div style={{ width: "100%", overflowX: "auto", marginTop: "30px", marginBottom: "50px" }}>
                <table className="table my_table competitorTable ">
                  <tbody>
                    <tr className="header-row">
                      <td className="text-center w-5 p-25-5">Pos</td>
                      <td className="text-center w-10 p-25-5">#</td>
                      <td className="mx-5 w-25 p-25-5">
                        {data.rows[0]?.refModel === "oireachtasTeam" ? "Team" : "Dancer"}
                      </td>
                      <td className="text-center w-10 p-25-5">R1+R2 std</td>
                      <td className="text-center w-10 p-25-5">Total IP</td>
                      <td className="w-33 p-25-5">Status</td>
                    </tr>
                    {data.rows.map((r) => (
                      <tr key={r.ref} style={{ background: "#FFFFFF", fontSize: "12px", height: "31px" }}>
                        <td className="text-center w-5 grey-30 p-25-5">
                          {r.position ? `${r.position}${r.tied ? "T" : ""}` : r.ftc ? "FTC" : "—"}
                        </td>
                        <td className="text-center w-10 grey-30 p-25-5">{r.card}</td>
                        <td className="mx-5 w-25 grey-30 p-25-5">
                          {r.label}
                          <div className="grey-42" style={{ fontSize: "11px" }}>{r.schoolName}</div>
                        </td>
                        <td className="text-center w-10 grey-30 p-25-5">
                          {r.recallStanding ? `(${r.recallStanding})` : ""}
                        </td>
                        <td className="text-center w-10 grey-30 p-25-5">
                          <b>{r.totalPoints}</b>
                        </td>
                        <td className="w-33 grey-42 p-25-5">{statusText(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {!selected && (
            <div className="d-flex justify-content-center my-5">Select a competition</div>
          )}
        </div>
      )}
      <PublishConfirm ask={ask} onConfirm={answerAsk} onCancel={() => setAsk(null)} />
      <ToastContainer />
      <Help />
    </>
  );
};

export default OireachtasResults;
